/**
 * Keep salary component library (API) and structure-embedded components aligned.
 */

function normalizeCode(code, name = "") {
  const fromCode = String(code || "").trim().toUpperCase().replace(/\s+/g, "_");
  if (fromCode) return fromCode;
  return String(name || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

function mapApiToLibrary(row) {
  return {
    id: row.id,
    name: row.name || "",
    code: row.code || "",
    category:
      row.category === "EMPLOYER_BENEFIT" ? "BENEFIT" : row.category || "EARNING",
    notes: row.notes || "",
    sortOrder: row.sortOrder ?? 0,
    isActive: row.active !== false,
    tdsReferenceHeadId: row.tdsReferenceHeadId ?? row.tds_reference_head_id ?? null,
    tdsReferenceHead: row.tdsReferenceHead || null,
    flexiEnabled: !!(row.flexiEnabled ?? row.flexi_enabled),
    flexiConfigJson: row.flexiConfigJson ?? row.flexi_config_json ?? null,
  };
}

function categoryFromStructureComponent(comp) {
  if (comp?.categories?.reimbursement) return "REIMBURSEMENT";
  if (comp?.categories?.deduction) return "DEDUCTION";
  if (comp?.categories?.benefit) return "BENEFIT";
  if (comp?.category) {
    const c = String(comp.category).toUpperCase();
    if (c === "EMPLOYER_BENEFIT") return "BENEFIT";
    return c;
  }
  return "EARNING";
}

/** Collect unique structure components missing from the library (by code). */
export function findStructureComponentsMissingFromLibrary(structures = [], library = []) {
  const libCodes = new Set(
    (library || []).map((c) => normalizeCode(c.code, c.name)).filter(Boolean)
  );
  const missingByCode = new Map();

  (structures || []).forEach((structure) => {
    (structure.components || []).forEach((comp) => {
      const code = normalizeCode(comp.code, comp.name);
      if (!code || libCodes.has(code) || missingByCode.has(code)) return;
      missingByCode.set(code, {
        code,
        name: comp.name || code,
        category: categoryFromStructureComponent(comp),
        notes: "",
        sortOrder: Number(comp.orderNo) || 0,
        isActive: true,
      });
    });
  });

  return Array.from(missingByCode.values());
}

/** Rematch structure component rows onto library ids/names by code. */
export function rematchStructureComponentsToLibrary(structureComponents = [], library = []) {
  return (structureComponents || []).map((sc) => {
    const code = normalizeCode(sc.code, sc.name);
    const lib =
      (library || []).find((l) => normalizeCode(l.code, l.name) === code) ||
      (library || []).find(
        (l) => String(l.name || "").toLowerCase() === String(sc.name || "").toLowerCase()
      );
    if (!lib) return sc;
    return {
      ...sc,
      id: lib.id,
      name: lib.name,
      code: lib.code || code,
    };
  });
}

/** Extract selectedFormula blobs from structures into a formula list. */
export function extractFormulasFromStructures(structures = []) {
  const byId = new Map();
  (structures || []).forEach((structure) => {
    (structure.components || []).forEach((comp) => {
      const raw = comp?.selectedFormula;
      if (raw == null || raw === "") return;

      let f = raw;
      if (typeof raw === "string") {
        f = {
          id: `${normalizeCode(comp.code, comp.name)}:${raw}`,
          name: `${comp.name || "Component"} formula`,
          formula: raw,
          category: "earning",
        };
      } else if (typeof raw !== "object") {
        return;
      }

      const formulaText = String(f.formula || "").trim();
      if (!formulaText) return;

      const id =
        f.id != null
          ? String(f.id)
          : `${normalizeCode(comp.code, comp.name)}:${formulaText}`;
      if (byId.has(id)) return;
      byId.set(id, {
        id,
        name: f.name || comp.name || "Formula",
        description:
          f.description || `From structure: ${structure.name || ""}`.trim(),
        formula: formulaText,
        category: f.category || "earning",
        isActive: f.isActive !== false,
        variables: Array.isArray(f.variables) ? f.variables : [],
        validationErrors: [],
        createdAt: f.createdAt || new Date().toISOString(),
        updatedAt: f.updatedAt || new Date().toISOString(),
      });
    });
  });
  return Array.from(byId.values());
}

export function loadMergedFormulasFromStorage() {
  const parse = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const byKey = new Map();
  [...parse("savedFormulas"), ...parse("salaryFormulas")].forEach((f, idx) => {
    if (!f || typeof f !== "object") return;
    const id = f.id != null ? String(f.id) : `legacy-${idx}-${f.name || f.formula || ""}`;
    byKey.set(id, { ...f, id });
  });
  return Array.from(byKey.values());
}

export function persistFormulasToStorage(formulas) {
  const list = Array.isArray(formulas) ? formulas : [];
  localStorage.setItem("salaryFormulas", JSON.stringify(list));
  localStorage.setItem("savedFormulas", JSON.stringify(list));
  return list;
}

export { mapApiToLibrary, normalizeCode, categoryFromStructureComponent };
